import React, { useEffect, useRef } from 'react';
import { Tooltip } from 'bootstrap';

interface TooltipProps {
  children: React.ReactNode;
  title: string;
  placement?: 'auto' | 'top' | 'bottom' | 'left' | 'right' | undefined;
  additionalClass?: string;
}

let currentOpenTooltip: Tooltip | null = null;  // Global tracker for the currently open tooltip


const TooltipComponent: React.FC<TooltipProps> = ({ children, title, placement, additionalClass }) => {
  const nodeRef = useRef<HTMLDivElement | null>(null);
  const tooltipRef = useRef<Tooltip | null>(null);

  useEffect(() => {
    const node = nodeRef.current;
    
    if (node) {
      // Destroy the existing tooltip if there is one
      if (tooltipRef.current) {
        tooltipRef.current.dispose();// Clean up any previous tooltip
      }

      const tooltip = new Tooltip(node, {
          title: title,
          trigger: 'hover',
          html: true,
          placement: placement ? placement : 'top',
          customClass: `bc-text-start-tooltip${additionalClass ? ' ' + additionalClass : ''}`,
          //customClass: additionalClass ? 'bc-text-start-tooltip bc-text-limit-3-tooltip' : 'bc-text-start-tooltip',
      });

      tooltipRef.current = tooltip;

      const showTooltip = (e: Event) => {
        // Close the currently open tooltip (if any) before showing the new one
        if (currentOpenTooltip && currentOpenTooltip !== tooltip) {
          currentOpenTooltip.hide();
          currentOpenTooltip = null;
        }
        tooltip.show();
        currentOpenTooltip = tooltip; // Track the open tooltip
      };

      const hideTooltip = () => {
        tooltip.hide();
        currentOpenTooltip = null;
      };

      node.addEventListener('mouseenter', showTooltip);
      node.addEventListener('mouseleave', hideTooltip);
      node.addEventListener('focus', hideTooltip);
      node.addEventListener('click', hideTooltip);

      return () => {
        if (node) {
          node.removeEventListener('mouseenter', showTooltip);
          node.removeEventListener('mouseleave', hideTooltip);
          node.removeEventListener('focus', hideTooltip);
          node.removeEventListener('click', hideTooltip);
        }

        // CRITICAL BUG FIX (Ghost Tooltips & Unmount Crashes):
        // 1. We MUST NOT check `nodeRef.current` here. In React 18, `ref.current` is set to null
        //    *before* the unmount cleanup runs. If we check it, this entire block is skipped, 
        //    leaving permanent "ghost" tooltips stranded in the DOM.
        // 2. We MUST NOT call `tooltipRef.current.hide()` here. `hide()` starts an async 150ms 
        //    transition. We want instant destruction on unmount.
        if (tooltipRef?.current) {
          try {
            const instance = tooltipRef.current as Tooltip & { _isWithActiveTrigger?: () => boolean };
            instance.dispose(); // Instantly destroy the tooltip and remove it from the DOM.
            
            // 3. WORKAROUND FOR BOOTSTRAP 5 BUG:
            //    If a user clicked the button to unmount it, the click event already started the 
            //    async 150ms `hide()` transition. When we call `.dispose()` above, the instance 
            //    is destroyed, but the 150ms timer is STILL running. 
            //    When the timer finishes, it calls `_isWithActiveTrigger()` on the destroyed instance, 
            //    causing a `TypeError: Cannot convert undefined or null to object` crash.
            //    Overriding this method forces the rogue timer to safely abort instead of crashing.
            instance._isWithActiveTrigger = () => true;
          } catch (error) {
            console.error('Error disposing tooltip:', error);
          } finally {
            tooltipRef.current = null;    // Clear the reference
          }
        }
      };
    }
  }, [title, placement]);

  return <span ref={nodeRef}>{children}</span>;
};

export default TooltipComponent;